import { useMemo } from 'react';

import type { User } from '@tasks/contracts';

import { Avatar } from '../../components/Avatar';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import type { TableColumn } from '../../components/Table';
import { formatDate } from '../../lib/format';
import {
  agentRoleLabels,
  globalRoleLabels,
  invitationStatusLabels,
  invitationStatusTones,
  userKindLabels,
} from '../../i18n/enums';
import styles from './AdminPages.module.css';

/**
 * Колонки таблицы администратора: пользователь, роль, статус приглашения,
 * активность, действия (UC-USR-07: приглашённые видны отдельным бейджем).
 */
export interface UserColumnActions {
  readonly onEdit: (user: User) => void;
  readonly onToggleActive: (variables: { readonly id: string; readonly isActive: boolean }) => void;
  /** Повторная отправка приглашения (UC-USR-06 AC5). */
  readonly onResend: (invitationId: string) => void;
  /** Отзыв приглашения (UC-USR-06 AC6). */
  readonly onRevoke: (invitationId: string) => void;
  readonly isInvitationPending: boolean;
}

export function useUserColumns(actions: UserColumnActions): readonly TableColumn<User>[] {
  return useMemo(
    () => [
      {
        id: 'user',
        header: 'Пользователь',
        render: (user) => (
          <span className={styles['userCell']}>
            <Avatar user={user} size="sm" />
            <span>
              <span>{user.displayName}</span>
              <br />
              <span className="u-muted u-mono">@{user.login}</span>
            </span>
          </span>
        ),
      },
      {
        id: 'email',
        header: 'Email',
        width: '220px',
        // У AI агента почты нет: остаётся прочерк, сервер виден в карточке.
        render: (user) => <span className="u-muted">{user.email ?? '—'}</span>,
      },
      {
        id: 'role',
        header: 'Роль',
        width: '190px',
        // Агент — это тип сотрудника и специализация, глобальной роли у него нет.
        render: (user) =>
          user.kind === 'ai_agent' ? (
            <span className={styles['kindCell']}>
              <Badge tone="primary">{userKindLabels[user.kind]}</Badge>
              {user.agentRole === null ? null : (
                <Badge tone="neutral">{agentRoleLabels[user.agentRole]}</Badge>
              )}
            </span>
          ) : (
            <Badge tone={user.role === 'admin' ? 'primary' : 'neutral'}>{globalRoleLabels[user.role]}</Badge>
          ),
      },
      {
        id: 'invitation',
        header: 'Приглашение',
        width: '180px',
        render: (user) => (
          <span className={styles['invitationCell']}>
            {user.invitationStatus ? (
              <Badge tone={invitationStatusTones[user.invitationStatus]}>
                {invitationStatusLabels[user.invitationStatus]}
              </Badge>
            ) : (
              <span className="u-muted">—</span>
            )}
            {user.invitationStatus === 'pending' || user.invitationStatus === 'expired' ? (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={actions.isInvitationPending || user.invitationId === null || user.invitationId === undefined}
                  onClick={() => {
                    if (user.invitationId !== null && user.invitationId !== undefined) {
                      actions.onResend(user.invitationId);
                    }
                  }}
                >
                  Отправить повторно
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={actions.isInvitationPending || user.invitationId === null || user.invitationId === undefined}
                  onClick={() => {
                    if (user.invitationId !== null && user.invitationId !== undefined) {
                      actions.onRevoke(user.invitationId);
                    }
                  }}
                >
                  Отозвать
                </Button>
              </>
            ) : null}
          </span>
        ),
      },
      {
        id: 'createdAt',
        header: 'Создан',
        width: '120px',
        render: (user) => <span className="u-muted">{formatDate(user.createdAt)}</span>,
      },
      {
        id: 'active',
        header: 'Активен',
        width: '110px',
        render: (user) => (
          <input
            type="checkbox"
            checked={user.isActive}
            aria-label={`Активность ${user.displayName}`}
            onChange={(event) =>
              actions.onToggleActive({ id: user.id, isActive: event.target.checked })
            }
          />
        ),
      },
      {
        id: 'actions',
        header: '',
        width: '120px',
        align: 'end',
        render: (user) => (
          <span className={styles['rowActions']}>
            <Button size="sm" variant="ghost" onClick={() => actions.onEdit(user)}>
              Изменить
            </Button>
          </span>
        ),
      },
    ],
    [actions],
  );
}
